import React, { useState, useMemo, useEffect } from "react";
import Loading from "../../customComponents/Loading";
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from "xlsx";
import { FaFileCsv, FaFileExcel, FaFilePdf, FaSearch } from "react-icons/fa";
import { formatDateIfValid } from "../../utils/helper";

interface DynamicTableProps<T> {
  data: T[];
  columns: {
    key: keyof T | string;
    label: string;
    render?: (value: any, row: T) => React.ReactNode;
    tooltip?: (value: any, row: T) => string; // 👉 custom tooltip
    isDate?: boolean; // 👉 force date formatting
    className?: string;
    filterable?: boolean;
    width?: string | number; // 👈 Add this
    isDecimal?: boolean;
    isAmount?: boolean;
  }[];
  pageSize?: number;
  loading?: boolean;
  title?: string;
  handleCopy?: (text: string) => void;
  onPageChange?: (page: number) => void;
  activeNUmber?: number;
  totalRecords?: number;
  totalPages?: number;
  onSearch?: any;
  searchKey?: string;
  hasTabs?: boolean;
  exportTypes?: ("excel" | "pdf" | "csv")[]; // ✅ Add exportTypes prop
  handleExportEventsCSV?: () => void; // ✅ Optional export handler for events CSV
  exportLoading?: boolean; // ✅ Loading state for export button
  exportCsvTitle?: string; // ✅ Hover tooltip for CSV export button
  filterSlot?: React.ReactNode; // ✅ Custom filter controls rendered next to search
}

const SearchIcon = FaSearch as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

export default function DynamicTable<T extends { id?: number | string }>({
  data,
  columns,
  pageSize = 50,
  loading = false,
  title = "",
  onPageChange,
  activeNUmber = 1,
  totalRecords = data.length,
  totalPages = Math.max(1, Math.ceil(totalRecords / pageSize)),
  onSearch,
  searchKey,
  hasTabs=true,
  exportTypes = ["csv", "excel", "pdf"], // ✅ Default to empty array
  handleExportEventsCSV,
  exportLoading,
  exportCsvTitle = "Export CSV",
  filterSlot,
}: DynamicTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState(searchKey ? searchKey : "");
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  );

  // ✅ Active page from parent or local
  const activePage = onPageChange ? activeNUmber : currentPage;

  const [gotoPage, setGotoPage] = useState(activePage);
  const [pageError, setPageError] = useState("");
  const bodyHeight = hasTabs
    ? "max-h-[calc(100vh-297px)]"
    : "max-h-[calc(100vh-200px)]";

  const FIXED_WIDTH_COLS = ["eventId", "id", "userId", "hostUserId", "taxAmount"];
  const IGNORE_DECIMAL_COLS = [
    "id",
    "eventId",
    "userId",
    "hostUserId",
    "referenceId",
    "phone",
    "phoneNumber",
    "mobile",
    "mobileNumber",
    "contact",
    "phone_no",
  ];

  useEffect(() => {
    setSearch(searchKey || "");
  }, [title, searchKey]);

  useEffect(() => {
    setGotoPage(activePage);
  }, [activePage]);

  // ✅ Page change
  const handlePageChange = (page: number) => {
    if (onPageChange) onPageChange(page);
    else setCurrentPage(Math.min(Math.max(1, page), totalPages));
  };
  // ✅ Export XLSX
  const exportToExcel = () => {
    const ws = XLSXUtils.json_to_sheet(data);
    const wb = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(wb, ws, "Sheet1");
    XLSXWriteFile(wb, `${title || "data"}.xlsx`);
  };

  // ✅ Export PDF
  const exportToPDF = async () => {
    // Dynamically import jsPDF and the autoTable plugin
    const jsPDFModule = await import("jspdf");
    const autoTableModule = await import("jspdf-autotable");

    // Ensure plugin is registered
    const doc = new jsPDFModule.jsPDF();

    // Exclude "Action" column
    const exportableColumns = columns.filter(
      (col) =>
        !["actions", "action"].includes(String(col.key).toLowerCase()) &&
        col.label.toLowerCase() !== "actions",
    );

    const tableColumn = exportableColumns.map((col) => col.label);
    const tableRows = data.map((row) =>
      exportableColumns.map((col) => {
        const value = col.key && col.key in row ? row[col.key as keyof T] : "-";
        return typeof value === "string" || typeof value === "number"
          ? value
          : "";
      }),
    );

    // ✅ Use the plugin correctly
    // @ts-ignore
    autoTableModule.default(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: "grid",
      styles: { fontSize: 9 },
    });

    doc.save(`${title || "data"}.pdf`);
  };
  const IconButton = ({
    Icon,
    onClick,
    title,
    colorClass = "bg-indigo-500",
  }: {
    Icon: React.ElementType<any, any> | null;
    onClick: () => void;
    title: string;
    colorClass?: string;
  }) => (
    <button
      onClick={onClick}
      className={`p-2 ${colorClass} text-white rounded hover:opacity-90 shadow-md flex items-center justify-center`}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  const TruncatedText = ({
    text,
    limit = 25,
  }: {
    text: string;
    limit?: number;
  }) => {
    const [expanded, setExpanded] = useState(false);
    if (!text || text.length <= limit) return <span> {text} </span>;

    return (
      <span className="inline-flex items-center justify-between">
        {expanded ? text : text.slice(0, limit) + "... "}
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-indigo-500 hover:underline text-xs font-medium pl-2"
        >
          {expanded ? " See Less" : "  See More"}
        </button>
      </span>
    );
  };

  return (
    <div className="w-full">
      <div className="flex lex-wrap justify-between items-center">
        {title && <h4 className="text-md font-bold mb-4">{title}</h4>}{" "}
        {totalRecords > 0 && (
          <h2 className="text-md font-bold mb-4">Total - {totalRecords}</h2>
        )}
      </div>

          {/* 🔍 Search & Export Controls */}
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              {onSearch && (
                <div className="relative w-72">
                  <input
                    key={title}
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && onSearch) {
                        onSearch(search.trim());
                      }
                    }}
                    className="px-4 py-2 pr-10 border-2 border-[#1b2431]/40 rounded-lg w-full bg-white text-sm text-gray-700 shadow-sm shadow-[#1b2431]/10 placeholder:text-[#1b2431]/50 hover:border-[#1b2431]/70 focus:outline-none focus:ring-2 focus:ring-[#1b2431]/30 focus:border-[#1b2431] transition"
                  />

                  {/* ❌ Clear button */}
                  {search && (
                    <button
                      onClick={() => {
                        setSearch("");
                        onSearch && onSearch("");
                      }}
                      className="absolute right-8 top-1/2 -translate-y-1/2 font-bold text-red-400 hover:text-red-600 focus:outline-none text-xs px-2"
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}

                  {/* 🔍 Search Icon Button */}
                  <button
                    onClick={() => onSearch?.(search.trim())}
                    disabled={!search.trim()}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition
            ${
              search.trim()
                ? "bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
                    title="Search"
                  >
                    <SearchIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              {filterSlot}
            </div>

            {data.length > 0 && exportTypes.length > 0 && (
              <div className="flex gap-2">
                {exportTypes.includes("excel") && (
                  <IconButton
                    Icon={FaFileExcel}
                    onClick={exportToExcel}
                    title="Export Excel"
                    colorClass="bg-indigo-500"
                  />
                )}

                {exportTypes?.includes("csv") && (
                  <button
                    onClick={
                      handleExportEventsCSV
                        ? handleExportEventsCSV
                        : exportToExcel
                    }
                    disabled={exportLoading}
                    className={`p-2 rounded shadow-md flex items-center justify-center
      ${
        exportLoading
          ? "bg-gray-400 cursor-not-allowed"
          : "bg-green-500 hover:opacity-90 text-white"
      }`}
                    title={exportCsvTitle}
                  >
                    {exportLoading ? (
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                    ) : (
                      React.createElement(FaFileCsv as any, {
                        className: "w-4 h-4",
                      })
                    )}
                  </button>
                )}

                {exportTypes.includes("pdf") && (
                  <IconButton
                    Icon={FaFilePdf}
                    onClick={exportToPDF}
                    title="Export PDF"
                    colorClass="bg-red-500"
                  />
                )}
              </div>
            )}
          </div>

      {/* 🧾 Table */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-lg pointer-events-auto">
            <Loading message={`Loading ${title}...`} size="medium" />
          </div>
        )}
        <div
          className={`transition-opacity ${
            loading ? "opacity-50 pointer-events-none select-none" : ""
          }`}
        >
          <div className="bg-white shadow-md rounded-lg overflow-auto scrollbar-custom flex flex-col h-[75vh]">
            {" "}
            {/* 🧾 Table header + body */}
            <div
              className={`flex-1 overflow-auto scrollbar-custom ${bodyHeight}`}
            >
              <table className="min-w-max text-sm text-left text-gray-600 w-full border-collapse">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs sticky top-0 z-10">
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={`${String(col.key)}-${title}`}
                        style={
                          col.key === "actions"
                            ? {}
                            : FIXED_WIDTH_COLS.includes(String(col.key))
                              ? { width: "100px", maxWidth: "100px" }
                              : { width: "190px", maxWidth: "190px" }
                        }
                        className={`px-4 py-3 whitespace-nowrap bg-gray-100 ${
                          col.className || ""
                        }`}
                      >
                        {col.isAmount ? `${col.label} ($)` : col.label}
                        {col.filterable && (
                          <input
                            type="text"
                            value={columnFilters[col.key as string] || ""}
                            onChange={(e) =>
                              setColumnFilters((prev) => ({
                                ...prev,
                                [col.key as string]: e.target.value,
                              }))
                            }
                            placeholder="Filter"
                            className="mt-1 w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {data.length > 0 ? (
                    data.map((row?: any, rowIndex?: any) => (
                      <tr
                        key={row.id || rowIndex}
                        className="border-b hover:bg-gray-50 transition-colors"
                      >
                        {columns.map((col) => {
                          // RAW VALUE
                          const rawValue =
                            col.key && col.key in row
                              ? row[col.key as keyof T]
                              : "-";
                          // FORMATTED VALUE

                          let displayValue = rawValue ? rawValue : "-";

                          // ⏳ Handle Date formatting
                          if (col.isDate && rawValue) {
                            displayValue = formatDateIfValid(rawValue);
                          }

                          // 🔢 Handle Decimal Formatting
                          else if (
                            col.isDecimal &&
                            !IGNORE_DECIMAL_COLS.includes(String(col.key)) &&
                            rawValue !== null &&
                            rawValue !== undefined &&
                            rawValue !== "" &&
                            rawValue !== "null" &&
                            rawValue !== "undefined" &&
                            !isNaN(Number(rawValue))
                          ) {
                            const num = Number(rawValue);
                            displayValue = num.toFixed(2);
                          }

                          // 💲 Amount formatting
                          if (
                            col.isAmount &&
                            displayValue !== "-" &&
                            !isNaN(Number(displayValue))
                          ) {
                            displayValue = `$${displayValue}`;
                          }

                          // TOOLTIP VALUE
                          const tooltipValue = col.tooltip
                            ? col.tooltip(rawValue, row)
                            : displayValue;
                          return (
                            <td
                              style={
                                col.key === "actions"
                                  ? {}
                                  : FIXED_WIDTH_COLS.includes(String(col.key))
                                    ? { width: "100px", maxWidth: "100px" }
                                    : { width: "190px", maxWidth: "190px" }
                              }
                              className={
                                col.key === "actions"
                                  ? `${col.className || ""} px-4 py-3`
                                  : `${
                                      col.className || ""
                                    } px-4 py-3 whitespace-nowrap overflow-hidden text-ellipsis`
                              }
                              title={
                                col.key === "actions" ? "" : tooltipValue || ""
                              }
                            >
                              {col.render
                                ? col.render(displayValue, row)
                                : String(displayValue ?? "-")}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-12 text-gray-500 min-w-full"
                      >
                        <div className="flex flex-col items-center justify-center space-y-2">
                          <p className="text-lg font-medium">
                            No data available.
                          </p>
                          <p className="text-sm text-gray-400">
                            Try adjusting your search.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* 📄 Pagination (sticky at bottom of container) */}
            {data.length > 0 && (
              <div className="sticky bottom-0 bg-white border-t z-10">
                <div className="flex justify-end items-center mt-2 p-2 gap-2 flex-wrap">
                  <button
                    disabled={activePage === 1}
                    onClick={() => handlePageChange(activePage - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Prev
                  </button>

                  {Array.from({ length: totalPages })
                    .map((_, i) => i + 1)
                    .filter(
                      (page) =>
                        page === 1 ||
                        page === totalPages ||
                        (page >= activePage - 2 && page <= activePage + 2),
                    )
                    .map((page, idx, arr) => (
                      <React.Fragment key={page}>
                        {idx > 0 && arr[idx] !== arr[idx - 1] + 1 && (
                          <span className="px-2">...</span>
                        )}
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 border rounded ${
                            activePage === page
                              ? "bg-indigo-500 text-white"
                              : ""
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}

                  <button
                    disabled={activePage >= totalPages}
                    onClick={() => handlePageChange(activePage + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>

                  <div className="flex items-center ml-4 gap-2">
                    <span>Go to page:</span>
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={gotoPage}
                      onChange={(e: any) => {
                        setPageError("");
                        setGotoPage(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const val = Number(gotoPage);
                          if (!val || val < 1 || val > totalPages) {
                            setPageError(
                              `Enter a value between 1 and ${totalPages}`,
                            );
                            return;
                          }
                          handlePageChange(val);
                        }
                      }}
                      className="w-16 px-2 py-1 border rounded text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />

                    {pageError && (
                      <span className="text-red-500 text-sm">{pageError}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
